import React from "react";
import {
  IconCrown,
  IconCalendar,
  IconCreditCard,
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconShieldCheck,
  IconChartBar,
  IconRocket,
} from "@tabler/icons-react";
import PlanServices from "../../../services/PlanServices";

const CurrentPlanCard = ({ plan, subscription, features = [] }) => {
  // =============================================== //
  // ESTADO: SEM PLANO                              //
  // =============================================== //
  if (!plan) {
    return (
      <div className="plan-hero-card fade-in">
        <div className="plan-hero-gradient" style={{ background: 'linear-gradient(135deg, #6b7280, #4b5563)' }}>
          <div className="plan-hero-content">
            <div className="plan-hero-info">
              <div className="plan-hero-badge">
                <IconAlertCircle size={14} />
                Sem plano ativo
              </div>
              <h2 className="plan-hero-name">Nenhum Plano</h2>
              <p className="plan-hero-description">
                Você ainda não possui um plano ativo. Escolha um plano abaixo para começar.
              </p>
            </div>
            <div className="plan-hero-status">
              <button
                className="plan-card-button primary"
                onClick={() => window.location.href = "/billing"}
              >
                <IconCreditCard size={18} />
                Escolher um Plano
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =============================================== //
  // PREPARAÇÃO DOS DADOS                           //
  // =============================================== //
  const statusText = PlanServices.getPlanStatusText(plan.status);
  const recurrencyText = plan.recurrency
    ? PlanServices.getRecurrencyText(plan.recurrency)
    : null;

  const isNoPlan = plan.status === "NO_PLAN";
  const isPlanActive = plan.status === "ACTIVE";
  const isTrialExpired = !!plan.trial_expired;
  const isTrialActive = !!plan.is_trial && !isTrialExpired;
  const daysRemaining = typeof plan.days_remaining === 'number' ? plan.days_remaining : null;

  // Separar features: com limite (mostrar barra) e sem limite (mostrar check)
  const enabledFeatures = features.filter(f => f.is_enabled);
  const featuresWithLimit = enabledFeatures.filter(f => f.free_limit || f.premium_limit);
  const featuresWithoutLimit = enabledFeatures.filter(f => !f.free_limit && !f.premium_limit);

  const handleScrollToPlans = () => {
    if (typeof window === 'undefined') return;
    const grid = document.querySelector('.plans-grid');
    if (grid && grid.scrollIntoView) {
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Determinar cor do gradient baseado no status
  const getGradientByStatus = () => {
    if (isTrialExpired) return 'linear-gradient(135deg, #ef4444, #dc2626)';
    if (isTrialActive) return 'linear-gradient(135deg, #f59e0b, #d97706)';
    if (isPlanActive) return 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
    if (plan.status === 'SUSPENDED') return 'linear-gradient(135deg, #f59e0b, #d97706)';
    if (plan.status === 'CANCELED') return 'linear-gradient(135deg, #ef4444, #dc2626)';
    if (plan.status === 'EXPIRED') return 'linear-gradient(135deg, #6b7280, #4b5563)';
    return 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
  };

  // Determinar classe do badge de status
  const getStatusClass = () => {
    if (isPlanActive) return 'active';
    if (plan.status === 'SUSPENDED') return 'suspended';
    if (plan.status === 'CANCELED') return 'canceled';
    if (plan.status === 'EXPIRED') return 'expired';
    return 'active';
  };

  // =============================================== //
  // RENDER                                         //
  // =============================================== //
  return (
    <div className="plan-hero-card fade-in">
      {/* Header Gradient */}
      <div className="plan-hero-gradient" style={{ background: getGradientByStatus() }}>
        <div className="plan-hero-content">
          <div className="plan-hero-info">
            <div className="plan-hero-badge">
              {isTrialActive || isTrialExpired ? (
                <IconClock size={14} />
              ) : (
                <IconCrown size={14} />
              )}
              {isTrialExpired
                ? 'Período de teste expirado'
                : isTrialActive
                  ? 'Período de teste'
                  : 'Seu Plano Atual'}
            </div>
            <h2 className="plan-hero-name">{plan.name}</h2>
            <p className="plan-hero-description">
              {plan.description || 'Aproveite todos os recursos do seu plano'}
            </p>
          </div>
          <div className="plan-hero-status">
            <span className={`plan-status-badge ${getStatusClass()}`}>
              <IconShieldCheck size={16} />
              {isTrialActive
                ? `${daysRemaining ?? '—'} ${daysRemaining === 1 ? 'dia restante' : 'dias restantes'}`
                : isTrialExpired
                  ? 'Expirado'
                  : statusText}
            </span>
          </div>
        </div>
      </div>

      {/* Banner de trial */}
      {(isTrialActive || isTrialExpired) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 20px',
            background: isTrialExpired ? '#fef2f2' : '#fffbeb',
            borderLeft: `4px solid ${isTrialExpired ? '#dc2626' : '#f59e0b'}`,
            color: isTrialExpired ? '#7f1d1d' : '#78350f',
            fontSize: '0.9rem',
          }}
        >
          {isTrialExpired ? (
            <IconAlertCircle size={20} style={{ flexShrink: 0 }} />
          ) : (
            <IconClock size={20} style={{ flexShrink: 0 }} />
          )}
          <div style={{ flexGrow: 1 }}>
            {isTrialExpired ? (
              <>
                <strong>Seu período de teste expirou.</strong>{' '}
                Contrate agora para continuar utilizando a plataforma sem interrupções.
              </>
            ) : (
              <>
                <strong>Você está em período de teste.</strong>{' '}
                {daysRemaining !== null
                  ? `${daysRemaining} ${daysRemaining === 1 ? 'dia restante' : 'dias restantes'}.`
                  : ''}{' '}
                Contrate agora para garantir o acesso ininterrupto.
              </>
            )}
          </div>
          <button
            type="button"
            onClick={handleScrollToPlans}
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: isTrialExpired ? '#dc2626' : '#f59e0b',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <IconRocket size={16} />
            Contratar agora
          </button>
        </div>
      )}

      {/* Métricas do Plano */}
      {!isNoPlan && (
        <div className="plan-metrics-grid">
          {/* Valor */}
          {(plan.price || subscription?.value) && (
            <div className="plan-metric-item">
              <div className="plan-metric-icon">
                <IconCreditCard size={22} />
              </div>
              <div className="plan-metric-content">
                <p className="plan-metric-label">Valor</p>
                <p className="plan-metric-value">
                  {PlanServices.formatCurrency(plan.price || subscription?.value)}
                  {recurrencyText && <span style={{ fontSize: '12px', opacity: 0.7 }}>/{recurrencyText.toLowerCase()}</span>}
                </p>
              </div>
            </div>
          )}

          {/* Próximo Vencimento */}
          {plan.next_billing_date && (
            <div className="plan-metric-item">
              <div className="plan-metric-icon">
                <IconClock size={22} />
              </div>
              <div className="plan-metric-content">
                <p className="plan-metric-label">Próximo Vencimento</p>
                <p className="plan-metric-value">{PlanServices.formatDate(plan.next_billing_date)}</p>
              </div>
            </div>
          )}

          {/* Data de Início */}
          {plan.started_at && (
            <div className="plan-metric-item">
              <div className="plan-metric-icon">
                <IconCalendar size={22} />
              </div>
              <div className="plan-metric-content">
                <p className="plan-metric-label">Início do Plano</p>
                <p className="plan-metric-value">{PlanServices.formatDate(plan.started_at)}</p>
              </div>
            </div>
          )}

          {/* Data de Expiração */}
          {plan.expiration_date && (
            <div className="plan-metric-item">
              <div className="plan-metric-icon">
                <IconCalendar size={22} />
              </div>
              <div className="plan-metric-content">
                <p className="plan-metric-label">Expira em</p>
                <p className="plan-metric-value">{PlanServices.formatDate(plan.expiration_date)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recursos do Plano */}
      {enabledFeatures.length > 0 && (
        <div className="plan-resources-section">
          <div className="plan-resources-header">
            <h3 className="plan-resources-title">
              <IconChartBar size={20} />
              Recursos do Plano
            </h3>
          </div>

          {/* Features COM limite - Progress bars */}
          {featuresWithLimit.length > 0 && (
            <div className="plan-resources-grid">
              {featuresWithLimit.map((feature) => {
                const limit = feature.free_limit || feature.premium_limit;
                const percentage = feature.usage_percentage || 0;
                const barClass = percentage >= 90 ? 'danger' : percentage >= 70 ? 'warning' : 'success';

                return (
                  <div key={feature.id} className="plan-feature-progress">
                    <div className="plan-feature-header">
                      <span className="plan-feature-name">
                        {feature.name}
                      </span>
                      <span className="plan-feature-usage">
                        {feature.current_usage || 0} / {limit}
                      </span>
                    </div>
                    <div className="plan-feature-bar">
                      <div
                        className={`plan-feature-bar-fill ${barClass}`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Features SEM limite - Checklist */}
          {featuresWithoutLimit.length > 0 && (
            <div className="plan-features-checklist">
              {featuresWithoutLimit.map((feature) => (
                <div key={feature.id} className="plan-feature-check">
                  <div className="plan-feature-check-icon">
                    <IconCheck size={14} />
                  </div>
                  <span className="plan-feature-check-name">{feature.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CurrentPlanCard;
